import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api } from './api';
import { setMoneyMasked } from './format';
import { useAuth } from './auth';

type PrivacyState = {
  /** True while figures are redacted. Starts true on every app open. */
  masked: boolean;
  /** True while the unlock prompt is up. */
  prompting: boolean;
  /** Whether the account has a PIN — decides PIN prompt vs password prompt. */
  pinSet: boolean;
  /** Opens the unlock prompt. */
  requestUnlock: () => void;
  /** Dismisses the prompt and leaves figures masked. */
  dismiss: () => void;
  hide: () => void;
  /** Both resolve on success and reject with the API error otherwise. */
  unlockWithPin: (pin: string) => Promise<void>;
  unlockWithPassword: (password: string) => Promise<void>;
};

const PrivacyContext = createContext<PrivacyState | null>(null);

/**
 * Masks every currency figure until the PIN (or, failing that, the account
 * password) is entered.
 *
 * Deliberately in-memory and defaulting to masked: reload, a new tab, or a
 * fresh boot all come up covered, so handing the app to someone — or opening
 * it in front of them — never exposes the numbers on its own. Persisting the
 * unlocked state would defeat the point.
 *
 * This is a shoulder-surfing guard, not an access control. The figures are
 * already in the page for a live session, so anyone with devtools can read
 * them. What it buys is that the screen alone gives nothing away.
 */
export function PrivacyProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [masked, setMasked] = useState(true);
  const [prompting, setPrompting] = useState(false);
  /** Whether the opening prompt has already been shown-and-answered. */
  const [greeted, setGreeted] = useState(false);

  // Applied during render, before children read the formatters. Assigning a
  // module global here is the design rather than an oversight: it is
  // idempotent, and it keeps the formatters plain functions that ~90 render
  // sites can call without a hook.
  setMoneyMasked(masked);

  const pinSet = Boolean(user?.pinSet);

  // A new session starts covered, so signing out and back in — or switching
  // accounts — never inherits the previous session's unlock.
  useEffect(() => {
    setMasked(true);
    setPrompting(false);
    setGreeted(false);
  }, [user?.id]);

  // Opening the app asks once: enter the PIN, or skip and browse masked.
  // Without a PIN there's nothing quick to ask for, so it stays out of the way
  // and the header toggle is the way in.
  //
  // `greeted` is set as soon as the session is known either way — otherwise
  // setting a PIN in Settings would flip `pinSet` and fire the opening prompt
  // right there, several screens from an app open.
  useEffect(() => {
    if (greeted || !user) return;
    setGreeted(true);
    if (user.pinSet) setPrompting(true);
  }, [greeted, user]);

  const hide = useCallback(() => setMasked(true), []);
  const dismiss = useCallback(() => setPrompting(false), []);
  const requestUnlock = useCallback(() => setPrompting(true), []);

  const unlockWithPin = useCallback(async (pin: string) => {
    await api('/auth/pin/verify', { method: 'POST', body: { pin } });
    setMasked(false);
    setPrompting(false);
  }, []);

  const unlockWithPassword = useCallback(async (password: string) => {
    await api('/auth/password/verify', { method: 'POST', body: { password } });
    setMasked(false);
    setPrompting(false);
  }, []);

  const value = useMemo(
    () => ({
      masked,
      prompting,
      pinSet,
      requestUnlock,
      dismiss,
      hide,
      unlockWithPin,
      unlockWithPassword,
    }),
    [masked, prompting, pinSet, requestUnlock, dismiss, hide, unlockWithPin, unlockWithPassword],
  );

  return <PrivacyContext.Provider value={value}>{children}</PrivacyContext.Provider>;
}

export function usePrivacy(): PrivacyState {
  const ctx = useContext(PrivacyContext);
  if (!ctx) throw new Error('usePrivacy must be used inside <PrivacyProvider>');
  return ctx;
}
